import { HttpService } from "@app/core/services/http/http.service";
import { Injectable, WritableSignal, inject, signal } from "@angular/core";
import { AuthenticationService } from "@app/core/services/authentication.service";
import { UserSettingsService } from "@app/core/services/user-settings.service";
import { APPLICATIONS } from "@app/shared/constants/applications.constants";
import { IPLUS_URL } from "@app/shared/constants/url.constants";
import {
  UserEventActivity,
  UserEventActivityRequestAdapter,
  UserEventActivityResponseAdapter,
  UserEventTypes,
  UserEventsAdapter,
  UserEventsList
} from "@app/shared/models/user-events.model";
import { map, of } from "rxjs";

@Injectable({
  providedIn: "root"
})
export class UserEventsService {
  private _favoritesSignal: WritableSignal<UserEventsList[]> = signal([]);
  private _myReportsSignal: WritableSignal<UserEventsList[]> = signal([]);
  private _onDemandSignal: WritableSignal<UserEventsList[]> = signal([]);
  private _ilhSignal: WritableSignal<UserEventsList[]> = signal([]);
  private _glhSignal: WritableSignal<UserEventsList[]> = signal([]);
  private _wcSignal: WritableSignal<UserEventsList[]> = signal([]);

  private http = inject(HttpService);
  private authService = inject(AuthenticationService);
  private userSettingsService = inject(UserSettingsService);

  /**
   * Fetch event list for specified type
   * @param eventType : @see UserEventTypes
   * @param applicationId : Leave empty for generic lists like Favorites, Reports, etc
   * @param limit: number of records to return. Default to 3
   * @returns
   */
  fetchEvents(eventType: string, applicationId: string = "") {
    if (!this.eventEnabled(eventType)) {
      return of([]);
    }

    const respAdapter = new UserEventsAdapter();
    return this.http
      .addDataParams({
        filterUserId: this.authService.credentials.username,
        filterApplicationId: applicationId,
        filterEventType: eventType,
        numberOfRecordsToSelect: this.recentActivitySettings.recentActivityMaxItems
      })
      .skipLoaderHandler()
      .get(IPLUS_URL.USER_EVENT_LIST)
      .pipe(map((response: any) => respAdapter.adapt(response)));
  }

  /**
   * Fetch all activity for a specific user
   * @param user
   * @returns
   */
  fetchUserEventActivity(data: UserEventActivity) {
    const reqAdapter = new UserEventActivityRequestAdapter();
    const respAdapter = new UserEventActivityResponseAdapter();
    return this.http
      .addDataParams(reqAdapter.adapt(data))
      .skipLoaderHandler()
      .get(IPLUS_URL.USER_EVENT_ACTIVITY_LIST)
      .pipe(map((response: any) => respAdapter.adapt(response)));
  }

  /**
   * Add event to signal based on eventType and appId
   * @param eventType
   * @param description
   * @param value
   * @param appId
   */
  addEvent(eventType: string, description: string, value: any, appId?: string, isLegacy?: boolean) {
    if (!this.eventEnabled(eventType)) {
      return;
    }
    const signal = this._getSignal(eventType, appId);

    // Check for duplicate in current event list
    const index = signal().findIndex((x) => x.eventType === eventType && x.item === value);
    if (index === 0) {
      return;
    }

    // If an event is found in the array we need to move it to the beginning of the array
    if (index > 0) {
      const events = [...signal()];
      events.splice(index, 1);
      events.unshift(signal()[index]);
      signal.set(events);
      return;
    }

    // Add new event to array
    const event: UserEventsList = {
      userId: this.authService.credentials.username,
      applicationId: appId,
      eventType: eventType,
      item: value,
      description: description,
      isLegacy: isLegacy,
      timestamp: ""
    };

    signal.update((values: UserEventsList[]) => [...[event], ...values]);
  }

  /**
   * Add event to signal based on eventType and appId
   * @param eventType
   * @param value
   * @param appId
   * @param writableSignal
   */
  removeEvent(eventType: string, value: any, appId?: string, writableSignal?: WritableSignal<UserEventsList[]>) {
    if (!this.eventEnabled(eventType)) {
      return;
    }

    const signal = writableSignal ?? this._getSignal(eventType, appId);
    signal.update((values: UserEventsList[]) => [...values.filter((x) => x.item !== value)]);
  }

  /**
   * Returns if the activity is enabled for the user
   * @param eventType
   * @returns
   */
  eventEnabled(eventType: string) {
    switch (eventType) {
      case UserEventTypes.MyReports: {
        return this.recentActivitySettings?.recentActivityReports;
      }
      case UserEventTypes.OnDemand: {
        return this.recentActivitySettings?.recentActivityOnDemand;
      }
      case UserEventTypes.Favorites: {
        return this.recentActivitySettings?.recentActivityFavorites;
      }
      case UserEventTypes.MenuItems: {
        return this.recentActivitySettings?.recentActivityMenuItems;
      }
      default: {
        return false;
      }
    }
  }

  get favoritesSignal(): WritableSignal<UserEventsList[]> {
    return this._favoritesSignal;
  }

  set favoritesSignal(data: UserEventsList[]) {
    this._favoritesSignal.set(data);
  }

  get myReportsSignal(): WritableSignal<UserEventsList[]> {
    return this._myReportsSignal;
  }

  set myReportsSignal(data: UserEventsList[]) {
    this._myReportsSignal.set(data);
  }

  get onDemandSignal(): WritableSignal<UserEventsList[]> {
    return this._onDemandSignal;
  }

  set onDemandSignal(data: UserEventsList[]) {
    this._onDemandSignal.set(data);
  }

  get ilhSignal(): WritableSignal<UserEventsList[]> {
    return this._ilhSignal;
  }

  set ilhSignal(data: UserEventsList[]) {
    this._ilhSignal.set(data);
  }

  get glhSignal(): WritableSignal<UserEventsList[]> {
    return this._glhSignal;
  }

  set glhSignal(data: UserEventsList[]) {
    this._glhSignal.set(data);
  }

  get wcSignal(): WritableSignal<UserEventsList[]> {
    return this._wcSignal;
  }

  set wcSignal(data: UserEventsList[]) {
    this._wcSignal.set(data);
  }

  private get recentActivitySettings() {
    return this.userSettingsService?.recentActivitySettings;
  }

  /**
   * Retrieve signal based on eventType and appId
   * @param eventType
   * @param appId
   * @returns
   */
  private _getSignal(eventType: string, appId?: string) {
    switch (eventType) {
      case UserEventTypes.MyReports: {
        return this._myReportsSignal;
      }
      case UserEventTypes.Favorites: {
        return this._favoritesSignal;
      }
      case UserEventTypes.OnDemand: {
        return this._onDemandSignal;
      }
      case UserEventTypes.MenuItems: {
        switch (appId) {
          case APPLICATIONS.INDIVIDUAL: {
            return this._ilhSignal;
          }
          case APPLICATIONS.GROUP: {
            return this._glhSignal;
          }
          case APPLICATIONS.WC: {
            return this._wcSignal;
          }
          default: {
            return signal<UserEventsList[]>([]);
          }
        }
      }
      default: {
        return signal<UserEventsList[]>([]);
      }
    }
  }
}
